import type { ChangeEvent } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import { FolderAdd, Information } from '@mint-lib/icons';
import { Button, typographyVariants } from '@mint-lib/tw-components';
import { SearchBar } from '@mint-lib/tw-components/patterns';
import { cn } from '@mint-lib/tw-utils';

type Props = {
  canCreateSharedFolder: boolean;
  searchValue: string;
  onCreateSharedFolder: () => void;
  onChangeSearch: (event: ChangeEvent<HTMLInputElement>) => void;
  onClearSearch: () => void;
};

const SharedFoldersHeader = ({
  canCreateSharedFolder,
  searchValue,
  onCreateSharedFolder,
  onChangeSearch,
  onClearSearch,
}: Props) => {
  const { t } = useTranslation('@mint-lib/ai-chat');

  return (
    <section className="flex flex-col gap-3" data-testid="shared-folders">
      <div
        className={cn(
          'uppercase',
          typographyVariants({ variant: 'h5', color: 'primary' }),
        )}
      >
        {t('Shared folders')}
      </div>

      <div className="flex flex-row flex-wrap items-center justify-between gap-3">
        {canCreateSharedFolder ? (
          <Button
            onClick={onCreateSharedFolder}
            data-dd-action-name="Knowledge base: create shared folder"
          >
            <FolderAdd />
            {t('Create shared folder')}
          </Button>
        ) : (
          <div className="flex flex-row items-center gap-2">
            <Information className="size-4 fill-text-secondary" />
            <p
              className={typographyVariants({
                variant: 'bodyShort01',
                color: 'secondary',
              })}
            >
              {t(
                'Use the knowledge base to get more helpful insights from our AI agents',
              )}
            </p>
          </div>
        )}

        <SearchBar
          placeholder={t('Search a file or a folder')}
          className="h-8 w-75 max-w-75"
          onChange={onChangeSearch}
          onClear={onClearSearch}
          value={searchValue}
        />
      </div>
    </section>
  );
};

export { SharedFoldersHeader };
